// frontend/src/contexts/WorkspaceContext.jsx
// Version complète avec gestion des exercices fiscaux

import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api, { setActiveWorkspaceId, setActiveFiscalYearId } from '../api/api';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { currentUser, tenantType, isAuthenticated } = useAuth();

  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ AJOUT: Gestion des exercices fiscaux
  const [fiscalYears, setFiscalYears] = useState([]);
  const [currentFiscalYear, setCurrentFiscalYear] = useState(null);
  const [fiscalYearsLoading, setFiscalYearsLoading] = useState(false);

  // ========== CHARGEMENT WORKSPACES ==========
  const loadWorkspaces = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/workspaces');
      const data = response.data;

      setWorkspaces(data.workspaces || []);

      // ✅ AUTO-SÉLECTION du workspace
      const savedWorkspaceId = localStorage.getItem('active_workspace_id');
      const availableWorkspaces = data.workspaces || [];

      if (availableWorkspaces.length === 0) {
        console.warn('⚠️  Aucun workspace disponible');
        setLoading(false);
        return;
      }

      let workspaceToActivate = null;

      // 1. Essayer de restaurer le workspace sauvegardé
      if (savedWorkspaceId) {
        workspaceToActivate = availableWorkspaces.find((w) => w._id === savedWorkspaceId);
      }

      // 2. Sinon, prendre le premier workspace disponible
      if (!workspaceToActivate) {
        workspaceToActivate = availableWorkspaces[0];
        console.log('✅ Auto-sélection du premier workspace:', workspaceToActivate.name);
      }

      // 3. Activer le workspace
      if (workspaceToActivate) {
        setActiveWorkspace(workspaceToActivate);
        setActiveWorkspaceId(workspaceToActivate._id);
        localStorage.setItem('active_workspace_id', workspaceToActivate._id);
        console.log('✅ Workspace actif:', workspaceToActivate.name);

        // ✅ Charger les exercices fiscaux du workspace
        await loadFiscalYears(workspaceToActivate._id);
      }

      setLoading(false);
    } catch (err) {
      console.error('❌ Erreur chargement workspaces:', err);
      setError(err.response?.data?.error || 'Erreur chargement workspaces');
      setLoading(false);
    }
  };

  // ========== CHARGEMENT EXERCICES FISCAUX ==========
  const loadFiscalYears = async (workspaceId) => {
    try {
      setFiscalYearsLoading(true);
      const response = await api.get(`/fiscal-years/workspace/${workspaceId}`);
      const years = response.data.fiscalYears || [];
      
      setFiscalYears(years);

      // Sélectionner l'exercice actif
      const savedFiscalYearId = localStorage.getItem('current_fiscal_year_id');
      let yearToActivate = null;

      // 1. Essayer de restaurer l'exercice sauvegardé
      if (savedFiscalYearId) {
        yearToActivate = years.find((y) => y._id === savedFiscalYearId);
      }

      // 2. Sinon, prendre l'exercice actif (isActive=true)
      if (!yearToActivate) {
        yearToActivate = years.find((y) => y.isActive);
      }

      // 3. Sinon, prendre le plus récent
      if (!yearToActivate && years.length > 0) {
        yearToActivate = years.sort((a, b) => b.year - a.year)[0];
      }

      if (yearToActivate) {
        setCurrentFiscalYear(yearToActivate);
        setActiveFiscalYearId(yearToActivate._id);
        localStorage.setItem('current_fiscal_year_id', yearToActivate._id);
        console.log('✅ Exercice fiscal actif:', yearToActivate.label);
      } else {
        setCurrentFiscalYear(null);
        setActiveFiscalYearId(null);
        localStorage.removeItem('current_fiscal_year_id');
        console.log('⚠️  Aucun exercice fiscal disponible');
      }

      setFiscalYearsLoading(false);
    } catch (err) {
      console.error('❌ Erreur chargement exercices fiscaux:', err);
      setFiscalYears([]);
      setCurrentFiscalYear(null);
      setFiscalYearsLoading(false);
    }
  };

  // ========== CHANGER DE WORKSPACE ==========
  const switchWorkspace = async (workspaceId) => {
    const workspace = workspaces.find((w) => w._id === workspaceId);
    if (!workspace) {
      console.error('❌ Workspace introuvable:', workspaceId);
      return;
    }

    setActiveWorkspace(workspace);
    setActiveWorkspaceId(workspaceId);
    localStorage.setItem('active_workspace_id', workspaceId);

    console.log('🔄 Changement de workspace:', workspace.name);

    // ✅ Charger les exercices du nouveau workspace
    await loadFiscalYears(workspaceId);

    // Recharger la page pour rafraîchir toutes les données
    window.location.reload();
  };

  // ========== CHANGER D'EXERCICE FISCAL ==========
  const switchFiscalYear = (fiscalYearId) => {
    const year = fiscalYears.find((y) => y._id === fiscalYearId);
    if (!year) {
      console.error('❌ Exercice fiscal introuvable:', fiscalYearId);
      return;
    }

    setCurrentFiscalYear(year);
    setActiveFiscalYearId(fiscalYearId);
    localStorage.setItem('current_fiscal_year_id', fiscalYearId);
    console.log('🔄 Changement d\'exercice fiscal:', year.label);

    // Recharger pour rafraîchir les données comptables
    window.location.reload();
  };

  // ========== CRÉER UN EXERCICE FISCAL ==========
  const createFiscalYear = async (data) => {
    try {
      const response = await api.post('/fiscal-years', {
        ...data,
        workspaceId: activeWorkspace._id,
      });

      // Recharger les exercices
      await loadFiscalYears(activeWorkspace._id);

      return { success: true, fiscalYear: response.data };
    } catch (err) {
      console.error('❌ Erreur création exercice:', err);
      return {
        success: false,
        error: err.response?.data?.error || 'Erreur création exercice',
      };
    }
  };

  // ========== ACTIVER UN EXERCICE FISCAL ==========
  const activateFiscalYear = async (fiscalYearId) => {
    try {
      await api.put(`/fiscal-years/${fiscalYearId}/activate`);

      // Recharger les exercices
      await loadFiscalYears(activeWorkspace._id);

      return { success: true };
    } catch (err) {
      console.error('❌ Erreur activation exercice:', err);
      return {
        success: false,
        error: err.response?.data?.error || 'Erreur activation exercice',
      };
    }
  };

  // ========== CRÉER UN WORKSPACE CLIENT ==========
  const createClientWorkspace = async (clientId, name) => {
    try {
      const response = await api.post('/workspaces/create-client-workspace', {
        clientId,
        name,
      });

      const newWorkspace = response.data.workspace;
      setWorkspaces((prev) => [...prev, newWorkspace]);

      return { success: true, workspace: newWorkspace };
    } catch (err) {
      console.error('❌ Erreur création workspace:', err);
      return {
        success: false,
        error: err.response?.data?.error || 'Erreur création workspace',
      };
    }
  };

  // ========== RAFRAÎCHIR LES WORKSPACES ==========
  const refresh = () => {
    loadWorkspaces();
  };

  // ========== RAFRAÎCHIR LES EXERCICES FISCAUX ==========
  const refreshFiscalYears = () => {
    if (activeWorkspace) {
      loadFiscalYears(activeWorkspace._id);
    }
  };

  // ========== EFFET AU MONTAGE ==========
  useEffect(() => {
    if (isAuthenticated) {
      loadWorkspaces();
    } else {
      setWorkspaces([]);
      setActiveWorkspace(null);
      setFiscalYears([]);
      setCurrentFiscalYear(null);
      setLoading(false);
    }
  }, [isAuthenticated, currentUser]);

  // ✅ Exposer les entitlements du workspace actif
  const workspaceAccountingEnabled = !!activeWorkspace?.entitlements?.accountingEnabled;
  const workspaceStockEnabled = !!activeWorkspace?.entitlements?.stockEnabled;
  const workspaceESignatureEnabled = !!activeWorkspace?.entitlements?.eSignatureEnabled;
  const workspaceCabinetManagementEnabled = !!activeWorkspace?.entitlements?.cabinetManagementEnabled;

  const value = {
    // Workspaces
    workspaces,
    activeWorkspace,
    loading,
    error,
    switchWorkspace,
    createClientWorkspace,
    refresh,
    tenantType,

    // ✅ Entitlements du workspace actif
    workspaceAccountingEnabled,
    workspaceStockEnabled,
    workspaceESignatureEnabled,
    workspaceCabinetManagementEnabled,

    // ✅ Exercices fiscaux
    fiscalYears,
    currentFiscalYear,
    fiscalYearsLoading,
    switchFiscalYear,
    createFiscalYear,
    activateFiscalYear,
    refreshFiscalYears,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

WorkspaceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};